import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../utils/api';
import { AppNavbar } from '../components/AppNavbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Check, X, Loader2, Users, Zap, Crown, Sparkles,
  Image, Film, FileText, Mail, Printer, LayoutGrid,
  Palette, Mic, Calendar, BarChart3, Shield, Headphones,
  Building2, ArrowRight
} from 'lucide-react';

const PLAN_FEATURES = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Get started — no credit card',
    price: 0,
    priceAnnual: 0,
    color: '#6B7280',
    gradient: 'from-gray-500 to-gray-600',
    icon: Sparkles,
    highlight: false,
    badge: null,
    limits: '7 posts + 1 active listing',
    features: [
      { text: '7 Posts total', included: true },
      { text: '1 Active Listing', included: true },
      { text: 'Magic URL Scraper + Clipper', included: true },
      { text: 'AI Caption Generator', included: true },
      { text: 'PNG Export', included: true },
      { text: 'Scheduled Posting', included: false },
      { text: 'Video Reel Editor', included: false },
      { text: 'Carousel & Story Creator', included: false },
      { text: 'AI Voiceovers', included: false },
      { text: 'AI Descriptions', included: false },
      { text: 'Print Center', included: false },
      { text: 'Brand Kit', included: false },
    ],
    cta: 'Current Plan',
    ctaDisabled: true,
  },
  starter: {
    id: 'starter',
    name: 'Starter',
    tagline: 'Everything you need to start',
    price: 19,
    priceAnnual: 15,
    annualTotal: 180,
    color: '#3B82F6',
    gradient: 'from-blue-500 to-blue-600',
    icon: Zap,
    highlight: false,
    badge: 'Best Value',
    limits: '30 posts/mo + 5 listings',
    features: [
      { text: '30 Posts per month', included: true },
      { text: '5 Active Listings', included: true },
      { text: 'Magic URL Scraper + Clipper', included: true },
      { text: 'Scheduled Posting (10 active)', included: true },
      { text: 'Video Reel Editor (SD)', included: true },
      { text: 'Carousel & Story Creator', included: true },
      { text: 'AI Caption Generator', included: true },
      { text: 'AI Voiceovers', included: true },
      { text: 'PNG + PDF Export', included: true },
      { text: 'Print Center (Flyers)', included: true },
      { text: 'Email Support', included: true },
      { text: 'AI Descriptions', included: false },
      { text: 'Brand Kit', included: false },
    ],
    ctaMonthly: 'starter_monthly',
    ctaAnnual: 'starter_annual',
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    tagline: 'Unlimited everything — go all in',
    price: 39,
    priceAnnual: 31,
    annualTotal: 372,
    color: '#8B5CF6',
    gradient: 'from-violet-500 to-purple-600',
    icon: Crown,
    highlight: true,
    badge: 'Most Popular',
    limits: 'Unlimited posts + listings',
    features: [
      { text: 'Unlimited Posts', included: true, bold: true },
      { text: 'Unlimited Active Listings', included: true, bold: true },
      { text: 'Magic URL Scraper + Clipper', included: true },
      { text: 'Scheduled Posting (Unlimited)', included: true },
      { text: 'Video Reel Editor (HD)', included: true },
      { text: 'Carousel & Story Creator', included: true },
      { text: 'AI Caption Generator', included: true },
      { text: 'AI Voiceovers', included: true },
      { text: 'AI Descriptions', included: true },
      { text: 'PNG + PDF + HD Video Export', included: true },
      { text: 'Print Center (Flyers + Brochures)', included: true },
      { text: 'Brand Kit', included: true },
      { text: 'Priority Support', included: true },
    ],
    ctaMonthly: 'pro_monthly',
    ctaAnnual: 'pro_annual',
  },
  team: {
    id: 'team',
    name: 'Team / Broker',
    tagline: 'Power your entire brokerage',
    price: 149,
    priceAnnual: 124,
    annualTotal: 1488,
    color: '#10B981',
    gradient: 'from-emerald-500 to-green-600',
    icon: Building2,
    highlight: false,
    badge: 'For Brokerages',
    limits: 'Up to 10 users (+$20/seat)',
    features: [
      { text: 'Everything in Pro', included: true, bold: true },
      { text: 'Up to 10 Team Seats', included: true, bold: true },
      { text: 'Additional seats at $20/user', included: true },
      { text: 'Shared Content Library & Team Assets', included: true },
      { text: 'Team Management, Roles & Permissions', included: true },
      { text: 'Brokerage Branding (White-label)', included: true },
      { text: 'Admin Analytics Dashboard', included: true },
      { text: 'Custom Template Uploads', included: true },
      { text: 'Bulk Content Generation', included: true },
      { text: 'Agent Performance Tracking', included: true },
      { text: 'Dedicated Account Manager', included: true },
    ],
    ctaMonthly: 'team_monthly',
    ctaAnnual: 'team_annual',
  },
};

// ──────────── Usage Meters ────────────
const METRIC_DEFS = [
  { key: 'posts', icon: 'FileText', label: 'Posts', usedKey: 'posts_used', limitKey: 'posts_limit' },
  { key: 'listings', icon: 'Building2', label: 'Active Listings', usedKey: 'listings_used', limitKey: 'listings_limit' },
  { key: 'scrapes', icon: 'Sparkles', label: 'Magic Scrapes', usedKey: 'scrapes_used', limitKey: 'scrapes_limit' },
  { key: 'scheduled', icon: 'Calendar', label: 'Scheduled Posts', usedKey: 'scheduled_used', limitKey: 'scheduled_limit' },
];

const ICON_MAP = { FileText, Building2, Sparkles, Calendar };

const UsageMeter = ({ used, limit, icon, label, accent = '#6366F1' }) => {
  const unlimited = limit === -1;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const isNearLimit = !unlimited && pct >= 80;
  const isAtLimit = !unlimited && used >= limit;
  const barColor = isAtLimit ? '#EF4444' : isNearLimit ? '#F59E0B' : accent;
  const Icon = ICON_MAP[icon] || Sparkles;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow" data-testid={`usage-meter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${barColor}15` }}>
            <Icon className="h-3.5 w-3.5" style={{ color: barColor }} />
          </div>
          <span className="text-xs font-medium text-gray-500 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{label}</span>
        </div>
      </div>
      <div className="flex items-baseline gap-1.5 mb-2">
        <span className="text-2xl font-bold tracking-tight tabular-nums" style={{ fontFamily: 'Outfit, sans-serif', color: isAtLimit ? '#EF4444' : '#111' }} data-testid={`usage-used-${label.toLowerCase().replace(/\s+/g, '-')}`}>{used}</span>
        <span className="text-sm text-gray-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
          / {unlimited ? <span className="text-emerald-500 font-semibold">∞</span> : limit}
        </span>
      </div>
      {!unlimited && (
        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, background: barColor }}
          />
        </div>
      )}
      {unlimited && (
        <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Unlimited</div>
      )}
    </div>
  );
};

const UsageMeters = ({ usage, currentPlan, onUpgrade }) => {
  const scrapesAtLimit = usage.scrapes_limit !== -1 && usage.scrapes_used >= usage.scrapes_limit;
  const postsAtLimit = usage.posts_limit !== -1 && usage.posts_used >= usage.posts_limit;
  const anyAtLimit = scrapesAtLimit || postsAtLimit;

  return (
    <div className="max-w-screen-md mx-auto mb-12" data-testid="usage-meters-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Your usage this month
          <span className="ml-2 inline-block px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600" data-testid="current-plan-badge">
            {currentPlan}
          </span>
        </h2>
        {anyAtLimit && (
          <button
            onClick={onUpgrade}
            className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600 hover:text-violet-700 transition-colors"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="usage-upgrade-cta"
          >
            Upgrade to unblock <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {METRIC_DEFS.map(m => (
          <UsageMeter
            key={m.key}
            used={usage[m.usedKey] ?? 0}
            limit={usage[m.limitKey] ?? 0}
            icon={m.icon}
            label={m.label}
            accent={m.key === 'scrapes' ? '#10B981' : m.key === 'posts' ? '#6366F1' : m.key === 'listings' ? '#F59E0B' : '#3B82F6'}
          />
        ))}
      </div>
      {scrapesAtLimit && (
        <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="scrape-limit-notice">
          You've hit your Magic Scrape limit for this month. <strong>Upgrade your plan</strong> or use the <strong>RealProp Clipper</strong> browser extension to keep scraping listings.
        </div>
      )}
    </div>
  );
};

export const SubscriptionPage = () => {
  const { user, organization } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [premiumCount, setPremiumCount] = useState(0);

  const orderRef = searchParams.get('order_ref');

  useEffect(() => { fetchBillingDetails(); }, []);

  // Live count of premium templates so the Pro card pricing reflects real catalog value.
  useEffect(() => {
    apiClient.get('/templates').then(res => {
      const ts = res.data?.templates || [];
      setPremiumCount(ts.filter(t => t.is_premium).length);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (orderRef) capturePayment(orderRef);
  }, [orderRef]);

  const fetchBillingDetails = async () => {
    try {
      const res = await apiClient.get('/plan/current');
      setBillingDetails(res.data);
    } catch {}
  };

  const capturePayment = async (ref) => {
    setCheckingStatus(true);
    try {
      // Get the PayPal order ID from our backend
      const statusRes = await apiClient.get(`/paypal/order-status/${ref}`);
      const paypalOrderId = statusRes.data.paypal_order_id;

      if (statusRes.data.payment_status === 'paid') {
        toast.success('Subscription already active!');
        setCheckingStatus(false);
        fetchBillingDetails();
        navigate('/subscription', { replace: true });
        return;
      }

      // Capture the payment
      const captureRes = await apiClient.post(`/paypal/capture-order/${paypalOrderId}`);
      if (captureRes.data.payment_status === 'paid') {
        toast.success(`Payment successful! You're now on the ${captureRes.data.plan_type} plan.`);
        fetchBillingDetails();
      } else {
        toast.error('Payment was not completed. Please try again.');
      }
    } catch (err) {
      toast.error('Error processing payment. Please contact support.');
    }
    setCheckingStatus(false);
    navigate('/subscription', { replace: true });
  };

  const handleSubscribe = async (planId) => {
    setLoading(planId);
    try {
      const res = await apiClient.post('/paypal/create-order', { plan_id: planId, origin_url: window.location.origin });
      if (res.data.approve_url) {
        window.location.href = res.data.approve_url;
      } else {
        toast.error('Failed to create PayPal order');
        setLoading(null);
      }
    } catch {
      toast.error('Failed to start checkout.');
      setLoading(null);
    }
  };

  const currentPlanType = billingDetails?.plan || organization?.plan_type || 'free';

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-violet-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Processing your payment...</h2>
          <p className="text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Please wait while we confirm your subscription.</p>
        </div>
      </div>
    );
  }

  const plans = Object.values(PLAN_FEATURES);

  return (
    <div className="min-h-screen bg-white">
      <AppNavbar />

      <main className="max-w-screen-xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Simple pricing, powerful results
          </h1>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Start free. Upgrade when you're ready. No contracts, cancel anytime.
          </p>
        </div>

        {/* Usage Meters */}
        {billingDetails?.usage && (
          <UsageMeters
            usage={billingDetails.usage}
            currentPlan={currentPlanType}
            onUpgrade={() => document.getElementById('plans-grid')?.scrollIntoView({ behavior: 'smooth' })}
          />
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-gray-100 rounded-full p-1" data-testid="billing-toggle">
            <button
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${billingCycle === 'monthly' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'}`}
              onClick={() => setBillingCycle('monthly')}
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="toggle-monthly"
            >Monthly</button>
            <button
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${billingCycle === 'annual' ? 'bg-black text-white shadow-sm' : 'text-gray-500 hover:text-black'}`}
              onClick={() => setBillingCycle('annual')}
              style={{ fontFamily: 'Manrope, sans-serif' }}
              data-testid="toggle-annual"
            >Annual <span className="text-emerald-500 ml-1 text-xs font-bold">Save 20%</span></button>
          </div>
        </div>

        {/* Plans Grid */}
        <div id="plans-grid" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="plans-grid">
          {plans.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentPlanType === plan.id;
            const price = billingCycle === 'monthly' ? plan.price : plan.priceAnnual;
            const isLoading = loading === (billingCycle === 'monthly' ? plan.ctaMonthly : plan.ctaAnnual);

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl flex flex-col transition-all duration-200 ${
                  plan.highlight
                    ? 'border-2 border-violet-500 shadow-xl shadow-violet-500/10 scale-[1.02]'
                    : 'border border-gray-200 hover:border-gray-300 hover:shadow-lg'
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                    <span
                      className="px-3 py-1 text-xs font-bold text-white rounded-full whitespace-nowrap"
                      style={{ background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)` }}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Header */}
                <div className="p-6 pb-4 text-center">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.gradient} flex items-center justify-center mx-auto mb-3`}
                  >
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.name}
                  </h3>
                  <p className="text-xs text-gray-400 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {plan.tagline}
                  </p>

                  {/* Price */}
                  <div className="mt-4">
                    {price === 0 ? (
                      <span className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>$0</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>${price}</span>
                        <span className="text-gray-400 text-sm">/mo</span>
                      </>
                    )}
                  </div>
                  {billingCycle === 'annual' && plan.annualTotal > 0 && (
                    <p className="text-xs mt-1" style={{ color: plan.color, fontFamily: 'Manrope, sans-serif' }}>
                      ${plan.annualTotal}/year
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {plan.limits}
                  </p>
                  {/* Premium template count — visible on Pro and Team cards as a tangible upgrade hook */}
                  {(plan.id === 'pro' || plan.id === 'team') && premiumCount > 0 && (
                    <div
                      className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
                      style={{
                        background: `${plan.color}12`,
                        color: plan.color,
                        fontFamily: 'Manrope, sans-serif',
                      }}
                      data-testid={`plan-${plan.id}-premium-templates`}
                    >
                      <Crown className="h-3 w-3" />
                      Unlocks {premiumCount} Premium templates
                    </div>
                  )}
                </div>

                {/* Features */}
                <div className="px-6 pb-4 flex-1">
                  <ul className="space-y-2.5">
                    {plan.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        {feat.included ? (
                          <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
                        ) : (
                          <X className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-300" />
                        )}
                        <span
                          className={`text-sm ${feat.included ? (feat.bold ? 'font-bold text-gray-900' : 'text-gray-600') : 'text-gray-300 line-through'}`}
                          style={{ fontFamily: 'Manrope, sans-serif' }}
                        >
                          {feat.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="p-6 pt-2">
                  {plan.id === 'free' ? (
                    <Button
                      variant="outline"
                      className="w-full h-11 rounded-xl"
                      disabled
                      data-testid="free-plan-btn"
                    >
                      {isCurrentPlan ? 'Current Plan' : 'Free Forever'}
                    </Button>
                  ) : (
                    <Button
                      className={`w-full h-11 rounded-xl text-white transition-all ${
                        plan.highlight ? 'shadow-lg hover:shadow-xl' : ''
                      }`}
                      style={{ background: `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)` }}
                      onClick={() => handleSubscribe(billingCycle === 'monthly' ? plan.ctaMonthly : plan.ctaAnnual)}
                      disabled={isLoading || isCurrentPlan}
                      data-testid={`subscribe-${plan.id}-btn`}
                    >
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : isCurrentPlan ? null : (
                        <ArrowRight className="h-4 w-4 mr-2" />
                      )}
                      {isCurrentPlan ? 'Current Plan' : `Get ${plan.name}`}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Social Proof */}
        <div className="text-center mt-12 mb-8">
          <p className="text-sm text-gray-400" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Trusted by real estate agents and brokerages nationwide
          </p>
        </div>

        {/* Feature Comparison */}
        <div className="max-w-4xl mx-auto mt-12">
          <h2 className="text-2xl font-bold text-center mb-8 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Compare Plans
          </h2>
          <div className="border border-gray-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left p-4 font-semibold text-gray-600">Feature</th>
                  <th className="p-4 font-semibold text-gray-500 text-center">Free</th>
                  <th className="p-4 font-semibold text-blue-600 text-center">Starter</th>
                  <th className="p-4 font-semibold text-violet-600 text-center">Pro</th>
                  <th className="p-4 font-semibold text-emerald-600 text-center">Team</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Post Limits', '7 total', '30/mo', 'Unlimited', 'Unlimited'],
                  ['Active Listings', '1', '5', 'Unlimited', 'Unlimited'],
                  ['Magic URL Scraper', true, true, true, true],
                  ['Clipper', true, true, true, true],
                  ['Scheduled Posting', false, '10 active', 'Unlimited', 'Unlimited'],
                  ['Video Reel Editor', false, 'SD', 'HD', 'HD'],
                  ['Carousel & Story Creator', false, true, true, true],
                  ['AI Caption Generator', true, true, true, true],
                  ['AI Voiceovers', false, true, true, true],
                  ['AI Descriptions', false, false, true, true],
                  ['Export Formats', 'PNG', 'PNG + PDF', 'PNG+PDF+HD', 'PNG+PDF+HD'],
                  ['Print Center (Flyers)', false, true, '+ Brochures', '+ Brochures'],
                  ['Brand Kit', false, false, true, true],
                  ['Team Seats', '1', '1', '1', 'Up to 10'],
                  ['Support', 'Community', 'Email', 'Priority', 'Dedicated'],
                ].map(([feature, ...vals], i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0">
                    <td className="p-4 font-medium text-gray-700">{feature}</td>
                    {vals.map((val, j) => (
                      <td key={j} className="p-4 text-center">
                        {val === true ? <Check className="h-4 w-4 text-green-500 mx-auto" /> :
                         val === false ? <X className="h-4 w-4 text-gray-300 mx-auto" /> :
                         <span className="text-gray-600 text-xs font-medium">{val}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mt-16 mb-8">
          <h2 className="text-2xl font-bold text-center mb-8 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {[
              { q: 'Can I start for free?', a: 'Yes! The Free plan gives you 7 posts and 1 active listing with no credit card required. Perfect for trying RealProp before committing.' },
              { q: 'What happens after my free posts run out?', a: 'You\'ll need to upgrade to Starter ($19/mo) or Pro ($49/mo) to continue creating content. Your existing content stays accessible.' },
              { q: 'Can I switch plans anytime?', a: 'Absolutely. Upgrade or downgrade anytime. Billing adjusts automatically — no penalties.' },
              { q: 'What\'s included in Team seats?', a: 'Each seat allows one agent to create, edit, and publish under your brokerage brand. All content is shared within the team library.' },
              { q: 'Is there a contract?', a: 'No contracts. Cancel anytime and keep access until the end of your billing period.' },
              { q: 'Do you offer annual billing?', a: 'Yes! Save 20% with annual billing on all paid plans.' },
            ].map(({ q, a }) => (
              <div key={q} className="border border-gray-200 rounded-xl p-5 hover:bg-gray-50 transition-colors">
                <h4 className="font-semibold text-sm mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{q}</h4>
                <p className="text-sm text-gray-500" style={{ fontFamily: 'Manrope, sans-serif' }}>{a}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};
